import { processAnswer } from './answer-processor';
import type { Question, Answer, QuestionnaireState } from '@/types';

export class FlowEngine {
  private state: QuestionnaireState;
  private availableQuestions: Question[] = [];
  private allQuestions: Question[];

  constructor(leadId: number, questions: Question[]) {
    this.allQuestions = questions;
    this.state = {
      leadId,
      currentQuestionIndex: 0,
      answers: [],
      startedAt: new Date()
    };
    this.buildQuestionFlow();
  }

  private buildQuestionFlow(): void {
    this.availableQuestions = this.allQuestions.filter(q => !q.conditionalOn);
  }

  getCurrentQuestion(): Question | null {
    if (this.state.currentQuestionIndex >= this.availableQuestions.length) {
      return null;
    }
    return this.availableQuestions[this.state.currentQuestionIndex];
  }

  submitAnswer(rawValue: string | string[] | boolean): void {
    const currentQuestion = this.getCurrentQuestion();
    if (!currentQuestion) return;

    const answer = processAnswer(currentQuestion, rawValue);
    this.state.answers.push(answer);

    this.checkAndAddConditionalQuestions(currentQuestion, answer);

    this.state.currentQuestionIndex++;
  }

  skipQuestion(): void {
    this.state.currentQuestionIndex++;
  }

  removeLastAnswer(): void {
    if (this.state.answers.length > 0) {
      this.state.answers.pop();
      if (this.state.currentQuestionIndex > 0) {
        this.state.currentQuestionIndex--;
      }
    }
  }

  navigateBackToQuestion(questionId: string): void {
    const idx = this.availableQuestions.findIndex(q => q.id === questionId);
    if (idx === -1) return;

    // Remove all answers from this question onwards
    const idsToRemove = new Set(this.availableQuestions.slice(idx).map(q => q.id));
    this.state.answers = this.state.answers.filter(a => !idsToRemove.has(a.questionId));

    // Rebuild availableQuestions: keep questions before idx, then restart from this question
    // using only base (non-conditional) questions to avoid stale conditionals
    const baseOnly = this.allQuestions.filter(q => !q.conditionalOn);
    const baseIdx = baseOnly.findIndex(q => q.id === questionId);
    const baseFromHere = baseIdx >= 0 ? baseOnly.slice(baseIdx) : [];

    this.availableQuestions = [
      ...this.availableQuestions.slice(0, idx),
      ...baseFromHere
    ];

    this.state.currentQuestionIndex = idx;
  }

  private checkAndAddConditionalQuestions(
    answeredQuestion: Question,
    answer: Answer
  ): void {
    const conditionalQuestions = this.allQuestions.filter(
      q =>
        q.conditionalOn?.questionId === answeredQuestion.id &&
        this.isConditionalTriggered(q.conditionalOn!, answer)
    );

    if (conditionalQuestions.length > 0) {
      const insertIndex = this.state.currentQuestionIndex + 1;
      this.availableQuestions.splice(insertIndex, 0, ...conditionalQuestions);
    }
  }

  private isConditionalTriggered(
    conditionalOn: NonNullable<Question['conditionalOn']>,
    answer: Answer
  ): boolean {
    if (conditionalOn.numericCheck) {
      const numValue = typeof answer.value === 'number'
        ? answer.value
        : parseFloat(String(answer.value));
      const { type, value } = conditionalOn.numericCheck;
      if (type === 'less_than') return numValue < value;
      if (type === 'greater_than') return numValue > value;
      return false;
    }
    if (conditionalOn.expectedValue !== undefined) {
      return this.matchesExpectedValue(conditionalOn.expectedValue, answer.value);
    }
    return false;
  }

  private matchesExpectedValue(
    expected: string,
    actual: string | string[] | number | boolean
  ): boolean {
    if (typeof actual === 'boolean') {
      return expected === String(actual);
    }
    if (Array.isArray(actual)) {
      return actual.includes(expected);
    }
    return String(actual) === expected;
  }

  isComplete(): boolean {
    return this.state.currentQuestionIndex >= this.availableQuestions.length;
  }

  getProgress(): { current: number; total: number; percentage: number } {
    const current = this.state.currentQuestionIndex;
    const total = this.availableQuestions.length;
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    return { current, total, percentage };
  }

  getState(): QuestionnaireState {
    return { ...this.state };
  }

  getAnswers(): Answer[] {
    return [...this.state.answers];
  }

  getAnswerByQuestionId(questionId: string): Answer | undefined {
    return this.state.answers.find(a => a.questionId === questionId);
  }

  complete(): QuestionnaireState {
    this.state.completedAt = new Date();
    return this.getState();
  }

  getAllAnsweredQuestions(): Array<{ question: Question; answer: Answer }> {
    return this.state.answers
      .map(answer => {
        const question = this.allQuestions.find(q => q.id === answer.questionId);
        if (question) {
          return { question, answer };
        }
        return null;
      })
      .filter((item): item is { question: Question; answer: Answer } => item !== null);
  }
}
